import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Mail, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Compact single-input subscribe form for the Hub.
 * Collapses to a "Get first dibs" button, expands inline on click.
 */
export default function HubSubscribe() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await api.post("/subscribers", { email: email.trim(), source: "hub" });
      setDone(true);
      toast.success("You're on the list. Watch your inbox.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  }

  if (done) {
    return (
      <div
        data-testid="hub-subscribe-done"
        className="mt-3 inline-flex items-center gap-2 text-[11px] sm:text-xs uppercase tracking-widest text-[var(--trio-primary)] font-bold"
      >
        <Check className="w-3.5 h-3.5" />
        You're on the list — welcome to Trio
      </div>
    );
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="hub-subscribe-open"
        className="mt-3 inline-flex items-center gap-2 text-[11px] sm:text-xs uppercase tracking-widest text-[var(--trio-text)]/70 hover:text-[var(--trio-primary)] border-b border-[var(--trio-text)]/30 hover:border-[var(--trio-primary)] pb-0.5 transition-colors"
      >
        <Mail className="w-3.5 h-3.5" />
        Get first dibs on specials + events
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="hub-subscribe-form"
      className="mt-3 flex items-center gap-2 w-full max-w-sm"
    >
      <input
        type="email"
        required
        autoFocus
        placeholder="you@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        data-testid="hub-subscribe-email"
        className="flex-1 bg-transparent border-b border-[var(--trio-text)] focus:border-[var(--trio-primary)] focus:outline-none text-sm py-2 placeholder:text-[var(--trio-muted)]"
      />
      <button
        type="submit"
        disabled={sending}
        data-testid="hub-subscribe-submit"
        className="inline-flex items-center gap-1.5 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-4 py-2 text-[11px] tracking-widest uppercase font-bold disabled:opacity-60 transition-colors"
      >
        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Join"}
      </button>
    </form>
  );
}
